import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Video, Clock, AlertCircle, Mic, Play, RotateCcw, ChevronRight, CheckCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtSec = (s) => {
  const m = Math.floor(s / 60);
  const ss = String(Math.floor(s % 60)).padStart(2, '0');
  return `${m}:${ss}`;
};

const AsyncInterview = () => {
  const { token } = useParams();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Flow state machine
  // welcome -> permissions -> lead_time -> ready_321 -> question_show -> recording -> review -> next or done
  const [stage, setStage] = useState('welcome');
  const [currentQ, setCurrentQ] = useState(0);
  const [responses, setResponses] = useState([]); // [{order, blob, url, duration_s, attempts}]
  const [leadRemaining, setLeadRemaining] = useState(0);
  const [count321, setCount321] = useState(3);
  const [recElapsed, setRecElapsed] = useState(0);
  const [recRemaining, setRecRemaining] = useState(0);
  const [retakesUsed, setRetakesUsed] = useState({}); // {questionIndex: count}
  const [submitting, setSubmitting] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState(null);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoRef = useRef(null);
  const recTimerRef = useRef(null);
  const leadTimerRef = useRef(null);

  // Fetch config
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/async-interviews/by-token/${token}`);
        if (res.data?.success) setConfig(res.data.data);
        else setError('Interview not found');
      } catch (e) {
        setError(e?.response?.data?.message || 'Interview not found');
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      // cleanup on unmount
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (recTimerRef.current) clearInterval(recTimerRef.current);
      if (leadTimerRef.current) clearInterval(leadTimerRef.current);
    };
  }, [token]);

  // Camera/mic permission
  const requestPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      setStage('lead_time');
      // Start lead time countdown
      setLeadRemaining(config.lead_time_seconds || 300);
      leadTimerRef.current = setInterval(() => {
        setLeadRemaining((s) => {
          if (s <= 1) { clearInterval(leadTimerRef.current); setStage('question_show'); return 0; }
          return s - 1;
        });
      }, 1000);
    } catch (e) {
      setError('We need access to your camera and microphone to continue. Please grant permissions and reload the page.');
    }
  };

  // Callback ref — re-attaches the live MediaStream every time a <video> element mounts.
  // Without this, switching React-rendered stages produces a black preview because
  // .srcObject was set on a now-unmounted DOM node.
  const attachLivePreview = useCallback((el) => {
    videoRef.current = el;
    if (el && streamRef.current && el.srcObject !== streamRef.current) {
      el.srcObject = streamRef.current;
      const playP = el.play?.();
      if (playP && typeof playP.catch === 'function') playP.catch(() => {});
    }
  }, []);

  const skipLeadTime = () => {
    if (leadTimerRef.current) clearInterval(leadTimerRef.current);
    setLeadRemaining(0);
    setStage('question_show');
  };

  // 3-2-1 countdown then start recording
  useEffect(() => {
    if (stage !== 'ready_321') return;
    setCount321(3);
    const iv = setInterval(() => {
      setCount321((c) => {
        if (c <= 1) { clearInterval(iv); startRecording(); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [stage]);

  const startRecording = () => {
    if (!streamRef.current) return;
    const q = config.questions[currentQ];
    const max = q.max_seconds || 60;
    chunksRef.current = [];
    let mimeType = 'video/webm;codecs=vp9,opus';
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = '';
    const rec = mimeType ? new MediaRecorder(streamRef.current, { mimeType }) : new MediaRecorder(streamRef.current);
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
      const url = URL.createObjectURL(blob);
      setResponses((prev) => {
        const next = [...prev];
        next[currentQ] = { order: currentQ, blob, url, duration_s: recElapsed };
        return next;
      });
      setStage('review');
      if (recTimerRef.current) clearInterval(recTimerRef.current);
    };
    recorderRef.current = rec;
    rec.start();
    setRecElapsed(0);
    setRecRemaining(max);
    setStage('recording');
    recTimerRef.current = setInterval(() => {
      setRecElapsed((s) => s + 1);
      setRecRemaining((r) => {
        if (r <= 1) { stopRecording(); return 0; }
        return r - 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    if (recTimerRef.current) clearInterval(recTimerRef.current);
  };

  const retake = () => {
    const q = config.questions[currentQ];
    if (!q.allow_retake) return;
    if ((retakesUsed[currentQ] || 0) >= 1) return;
    setRetakesUsed((prev) => ({ ...prev, [currentQ]: (prev[currentQ] || 0) + 1 }));
    setStage('ready_321');
  };

  const nextQuestion = () => {
    if (currentQ + 1 < config.questions.length) {
      setCurrentQ(currentQ + 1);
      setStage('question_show');
    } else {
      submitAll();
    }
  };

  const submitAll = async () => {
    setSubmitting(true);
    setStage('submitting');
    try {
      // Upload each blob to backend → it stores in Supabase Storage and triggers transcription
      const formData = new FormData();
      formData.append('token', token);
      responses.forEach((r, i) => {
        if (r && r.blob) {
          formData.append(`video_${i}`, r.blob, `q${i}.webm`);
          formData.append(`duration_${i}`, String(r.duration_s || 0));
        }
      });
      const res = await axios.post(`${API}/api/async-interviews/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      if (res.data?.success) {
        setSubmittedSummary(res.data.data);
        setStage('done');
        // Cleanup camera
        if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      } else {
        setError(res.data?.message || 'Submission failed');
        setStage('review');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Submission failed');
      setStage('review');
    } finally {
      setSubmitting(false);
    }
  };

  // ──────────────────────────── RENDER ────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900 mb-1">Interview link invalid</h1>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Expired/completed gates
  const now = Date.now();
  if (config.expires_at && new Date(config.expires_at).getTime() < now && config.status_detail !== 'completed') {
    return <FullPageMessage icon={<AlertCircle className="w-10 h-10 text-gray-400" />} title="This interview has expired" body="Please contact your recruiter if you believe this is a mistake." />;
  }
  if (config.status_detail === 'completed' && stage !== 'done') {
    return <FullPageMessage icon={<CheckCircle className="w-10 h-10 text-green-500" />} title="You've already completed this interview" body="Thanks — your recruiter has your responses." />;
  }
  if (config.opens_at && new Date(config.opens_at).getTime() > now) {
    return <FullPageMessage icon={<Clock className="w-10 h-10 text-blue-500" />} title="Not open yet" body={`This interview opens on ${new Date(config.opens_at).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`} />;
  }

  const q = config.questions[currentQ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-white p-4 sm:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
              <Video className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900">{config.title}</h1>
              <p className="text-[11px] text-gray-500">Video interview · {config.questions.length} question{config.questions.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {stage !== 'welcome' && stage !== 'done' && (
            <span className="text-xs text-gray-500">Question {currentQ + 1} / {config.questions.length}</span>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {/* WELCOME */}
          {stage === 'welcome' && (
            <div className="p-8 text-center">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Video className="w-7 h-7 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Welcome</h2>
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                You're about to take a short async video interview.<br />
                {config.questions.length} short question{config.questions.length !== 1 ? 's' : ''}, ~{Math.max(2, config.questions.length * 2)} minutes total.<br />
                There's a {Math.round((config.lead_time_seconds || 300) / 60)}-minute warm-up period — you can skip it when ready.
              </p>
              <button
                onClick={() => setStage('permissions')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium inline-flex items-center gap-2"
                data-testid="ai-begin"
              >
                Begin <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* PERMISSIONS */}
          {stage === 'permissions' && (
            <div className="p-8 text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mic className="w-7 h-7 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Camera & microphone</h2>
              <p className="text-sm text-gray-600 mb-6">Please allow access to your camera and microphone in your browser when prompted.</p>
              <button
                onClick={requestPermissions}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium"
                data-testid="ai-allow"
              >
                Allow camera & mic
              </button>
              {error && <p className="text-xs text-red-600 mt-3">{error}</p>}
            </div>
          )}

          {/* LEAD TIME */}
          {stage === 'lead_time' && (
            <div className="p-8 text-center">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Take a moment</h2>
              <p className="text-sm text-gray-600 mb-4">Your interview begins in</p>
              <div className="text-6xl font-bold text-blue-600 tabular-nums mb-6" data-testid="lead-countdown">{fmtSec(leadRemaining)}</div>
              <div className="rounded-lg bg-black overflow-hidden mb-4 max-w-sm mx-auto">
                <video ref={attachLivePreview} autoPlay muted playsInline className="w-full aspect-video" />
              </div>
              <button
                onClick={skipLeadTime}
                className="text-sm text-blue-600 hover:underline"
                data-testid="ai-start-now"
              >
                I'm ready — start now
              </button>
            </div>
          )}

          {/* 3-2-1 */}
          {stage === 'ready_321' && (
            <div className="p-8 text-center">
              <p className="text-sm text-gray-600 mb-4">{q.prompt_text}</p>
              <div className="text-7xl font-bold text-blue-600 tabular-nums" data-testid="count-321">{count321 || 'GO!'}</div>
              <div className="rounded-lg bg-black overflow-hidden mt-6 max-w-sm mx-auto">
                <video ref={attachLivePreview} autoPlay muted playsInline className="w-full aspect-video" />
              </div>
            </div>
          )}

          {/* RECORDING */}
          {stage === 'recording' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-sm font-medium text-gray-700">Recording</span>
                </div>
                <span className="text-sm text-gray-500 tabular-nums" data-testid="rec-timer">{fmtSec(recElapsed)} / {fmtSec((q.max_seconds || 60))}</span>
              </div>
              <p className="text-sm text-gray-800 mb-3 leading-relaxed">{q.prompt_text}</p>
              <div className="rounded-lg bg-black overflow-hidden">
                <video ref={attachLivePreview} autoPlay muted playsInline className="w-full aspect-video" />
              </div>
              {/* Time remaining bar */}
              <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-red-500 transition-all duration-1000"
                  style={{ width: `${Math.max(0, Math.min(100, (recRemaining / (q.max_seconds || 60)) * 100))}%` }}
                />
              </div>
              <div className="mt-4 flex justify-center">
                <button
                  onClick={stopRecording}
                  className="bg-gray-900 hover:bg-black text-white px-5 py-2 rounded-lg text-sm font-medium"
                  data-testid="ai-stop"
                >
                  Stop & continue
                </button>
              </div>
            </div>
          )}

          {/* REVIEW */}
          {stage === 'review' && (
            <div className="p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Review your answer</h2>
              <p className="text-xs text-gray-500 mb-3">Question {currentQ + 1}: {q.prompt_text}</p>
              {responses[currentQ]?.url && (
                <video src={responses[currentQ].url} controls className="w-full rounded-lg bg-black" />
              )}
              <div className="flex gap-2 mt-4">
                {q.allow_retake && (retakesUsed[currentQ] || 0) < 1 && (
                  <button
                    onClick={retake}
                    className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5"
                    data-testid="ai-retake"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Retake (1 left)
                  </button>
                )}
                <button
                  onClick={nextQuestion}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5"
                  data-testid="ai-next"
                >
                  {currentQ + 1 < config.questions.length ? 'Next question' : 'Finish & submit'}
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
            </div>
          )}

          {/* QUESTION SHOW (between Qs) */}
          {stage === 'question_show' && (
            <div className="p-8 text-center">
              <p className="text-xs text-gray-400 mb-2">Question {currentQ + 1} of {config.questions.length}</p>
              {q.video_url ? (
                <div className="mb-4">
                  <p className="text-xs text-purple-600 font-medium mb-2 inline-flex items-center gap-1">
                    <Video className="w-3 h-3" /> Your interviewer recorded a video for this question
                  </p>
                  <video
                    src={q.video_url}
                    controls
                    autoPlay
                    className="w-full rounded-lg bg-black max-w-md mx-auto"
                    data-testid="recruiter-prompt-video"
                  />
                  <p className="text-sm text-gray-700 mt-3 leading-relaxed">{q.prompt_text}</p>
                </div>
              ) : (
                <p className="text-lg text-gray-800 leading-relaxed mb-6">{q.prompt_text}</p>
              )}
              <p className="text-xs text-gray-500 mb-4">You'll have {q.max_seconds}s to answer. {q.allow_retake ? 'One retake allowed.' : 'No retakes.'}</p>
              <button
                onClick={() => setStage('ready_321')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium inline-flex items-center gap-2"
                data-testid="ai-start-question"
              >
                Start answering <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* SUBMITTING */}
          {stage === 'submitting' && (
            <div className="p-10 text-center">
              <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-sm font-medium text-gray-800">Uploading your answers…</p>
              <p className="text-xs text-gray-500 mt-1">This usually takes 30–60 seconds.</p>
            </div>
          )}

          {/* DONE */}
          {stage === 'done' && (
            <div className="p-10 text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Thank you!</h2>
              <p className="text-sm text-gray-600">Your interview has been submitted. The recruiter will follow up shortly.</p>
            </div>
          )}
        </div>

        <p className="text-center text-[10px] text-gray-400 mt-4">Powered by JobastiIQ</p>
      </div>
    </div>
  );
};

const FullPageMessage = ({ icon, title, body }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md text-center">
      <div className="flex justify-center mb-3">{icon}</div>
      <h1 className="text-lg font-semibold text-gray-900 mb-1">{title}</h1>
      <p className="text-sm text-gray-600">{body}</p>
    </div>
  </div>
);

export default AsyncInterview;
